import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Plus, Radio, AlertTriangle, Crown, Calendar } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Header from '../components/Header';
import { getMeets, deleteMeet as apiDeleteMeet, getPurchaseStatus, verifyMeetExists } from '../utils/api';
import { getSportLabel, normalizeSportType } from '../utils/sportConfig';

// Tailwind classes per sport so a coach running all three seasons can
// tell at a glance which card belongs to which sport. Kept light/subtle
// so it doesn't compete with the primary "Tap to Start" affordance.
const SPORT_BADGE_STYLES = {
  track: 'bg-blue-500/15 text-blue-100 border-blue-300/30',
  swim: 'bg-cyan-500/15 text-cyan-100 border-cyan-300/30',
  xc: 'bg-emerald-500/15 text-emerald-100 border-emerald-300/30',
};

export default function Dashboard({ onLogout }) {
  const navigate = useNavigate();
  const [upcomingMeet, setUpcomingMeet] = useState(null);
  const [liveMeet, setLiveMeet] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, meetId: null, meetName: '' });
  const [purchaseSummary, setPurchaseSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      try {
        // Load purchase status from backend
        const purchaseData = await getPurchaseStatus();
        setPurchaseSummary({
          isPremium: purchaseData.is_premium,
          meetsCreated: purchaseData.meets_created,
          freeMeetsRemaining: purchaseData.free_meets_remaining
        });
        
        // Load live meet state from localStorage (single-user mode)
        const storedLiveMeet = localStorage.getItem('foresight_live_meet');
        if (storedLiveMeet) {
          const parsed = JSON.parse(storedLiveMeet);
          setLiveMeet(parsed);
        }

        // Load meets from backend API
        const meets = await getMeets();
        const sortedMeets = [...meets].sort((a, b) => new Date(b.created) - new Date(a.created));

        // "Upcoming Meet" surfacing: the most recently created meet that
        // has NOT been started yet (no `live_meets` record on the
        // backend — see `has_been_started` in MeetResponse). This gets
        // surfaced in a prominent top-of-Dashboard banner so an OS sleep
        // / cold-kill between Create-Meet completion and Start Meet
        // doesn't bury the new meet in "Recent Meets".
        //
        // Rules (from product spec, May 2026):
        //   • Only the SINGLE most recently created unstarted meet gets
        //     the banner slot. Older unstarted meets remain in the main
        //     list below.
        //   • The currently-live meet (if any) is handled by the
        //     "Continue Live Meet" banner; upcoming-meet banner is for
        //     never-started meets only.
        //   • If the banner-surfaced meet is shown above, we hide it
        //     from Recent Meets to avoid double-listing it.
        const liveMeetId = storedLiveMeet
          ? (() => { try { return JSON.parse(storedLiveMeet).meetId; } catch { return null; } })()
          : null;
        const upcoming = sortedMeets.find(
          (m) => m.has_been_started === false
            && String(m.id) !== String(liveMeetId)
        ) || null;
        setUpcomingMeet(upcoming);

        // Verify live meet still exists on the backend.
        //
        // CRITICAL: never delete the live-meet pointer based on
        // `getMeets()` results. `getMeets` is filtered by auth state
        // (see backend `_meet_owner_filter`) and can return an incomplete
        // list during Supabase JWT expiry/refresh — which is exactly what
        // happens after a long offline stretch. Production repro (May
        // 2026): meet running >1.5h, device offline, JWT expired, user
        // reconnected → `getMeets` returned anonymous-scoped meets only
        // → old code purged the live-meet pointer → "Continue Live Meet"
        // button vanished, even though the meet was alive on the backend.
        //
        // The only source of truth for "does this meet still exist?" is
        // a direct `GET /api/meets/{id}` (public, auth-agnostic). We only
        // purge the pointer on an explicit 404. Anything else (200,
        // network error, transient backend hiccup) keeps the pointer.
        if (storedLiveMeet) {
          const parsed = JSON.parse(storedLiveMeet);
          const result = await verifyMeetExists(parsed.meetId);
          if (result.exists === false && result.reason === 'notfound') {
            // Server explicitly confirmed the meet no longer exists.
            localStorage.removeItem('foresight_live_meet');
            setLiveMeet(null);
          }
          // result.exists === true  → meet alive, do nothing.
          // result.exists === null  → unknown (offline / transient) — do
          //                           nothing; keep the pointer so the
          //                           user can still resume.
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        // No fallback rendering: recentMeets list was removed (Feb
        // 2026 cleanup), and upcoming-meet + live-meet pointers come
        // from getMeets / localStorage above — both already handled.
      }
      
      setIsLoading(false);
    };
    
    loadData();
  }, []);

  const handleDeleteClick = (meetId, meetName, e) => {
    e.stopPropagation(); // Prevent card click navigation
    setDeleteDialog({ open: true, meetId, meetName });
  };

  const confirmDeleteMeet = async () => {
    const meetId = deleteDialog.meetId;
    
    try {
      // Delete from backend API
      await apiDeleteMeet(meetId);
      
      // Clear live meet if this was the active one
      if (liveMeet && String(liveMeet.meetId) === String(meetId)) {
        setLiveMeet(null);
        localStorage.removeItem('foresight_live_meet');
      }
      
      // Update state
      if (upcomingMeet && String(upcomingMeet.id) === String(meetId)) {
        setUpcomingMeet(null);
      }
      setDeleteDialog({ open: false, meetId: null, meetName: '' });
      
      toast.success('Meet deleted successfully');
    } catch (error) {
      console.error('Failed to delete meet:', error);
      toast.error('Failed to delete meet');
      setDeleteDialog({ open: false, meetId: null, meetName: '' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header onLogout={onLogout} />

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4">
          {/* Create New Meet Schedule Button */}
          <div className="relative pt-2">
            {purchaseSummary && purchaseSummary.isPremium ? (
              <div className="absolute -top-0 right-4 z-10">
                <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-xs px-2 gap-1">
                  <Crown className="w-3 h-3" />
                  Premium
                </Badge>
              </div>
            ) : purchaseSummary && purchaseSummary.freeMeetsRemaining > 0 && (
              <div className="absolute -top-0 right-4 z-10">
                <Badge className="bg-green-500 hover:bg-green-500 text-white text-xs px-2">
                  {purchaseSummary.freeMeetsRemaining} free
                </Badge>
              </div>
            )}
            <Button 
              onClick={() => {
                // Nuke any wizard sessionStorage from a previous run so the
                // new wizard instance is a fresh "create", not a silent edit.
                // (CreateMeetFlow also clears on mount, but clearing here
                // before navigation removes a race where the first render
                // of MeetSummary reads stale `alreadyCreated=true`.)
                try {
                  sessionStorage.removeItem('foresight_wizard_form');
                  sessionStorage.removeItem('foresight_wizard_created_id');
                } catch (_) { /* ignore quota / unavailable */ }
                navigate('/create-meet');
              }}
              size="lg"
              className="w-full h-14 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all bg-primary hover:bg-primary/90 text-white font-bold"
              data-testid="create-meet-btn"
            >
              <Plus className="w-6 h-6 mr-2" />
              Create New Meet Schedule
            </Button>
          </div>

          {/* Upcoming Meet — surfaced when the most recently created meet
              has not yet been started. Sits above the Continue Live Meet
              banner so the very first thing a returning user sees after
              an OS sleep / cold-kill between Create-Meet and Start Meet is
              "your meet is here, tap to start". Hidden when a live meet
              is active (that banner takes priority) or when there is no
              unstarted meet.

              Two-line layout: meet name on top, scheduled date/time +
              "Tap to Start" on the secondary line. Coaches with multiple
              weekend meets queued up can verify at a glance that the
              Dashboard is surfacing the right one. */}
          {upcomingMeet && !(liveMeet && !liveMeet.isOrphaned) && (() => {
            // Build a date-fns Date from `meet.date` (YYYY-MM-DD) and
            // `meet.startTime` (HH:mm). Falls back gracefully when either
            // field is malformed — we still render the banner, just
            // without the timestamp.
            let scheduledLabel = null;
            if (upcomingMeet.date && upcomingMeet.startTime) {
              try {
                const parsed = parse(
                  `${upcomingMeet.date} ${upcomingMeet.startTime}`,
                  'yyyy-MM-dd HH:mm',
                  new Date()
                );
                if (isValid(parsed)) {
                  scheduledLabel = format(parsed, 'EEE MMM d · h:mm a');
                }
              } catch (_) { /* malformed date — render without timestamp */ }
            }
            const upcomingSport = normalizeSportType(upcomingMeet.sport_type);
            return (
              <Button
                onClick={() => navigate(`/meet/${upcomingMeet.id}`)}
                size="lg"
                className="w-full h-auto min-h-16 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white gap-3 px-3 flex items-center"
                data-testid="upcoming-meet-btn"
              >
                <Calendar className="w-5 h-5 flex-shrink-0" />
                <div className="flex flex-col items-start text-left flex-1 min-w-0">
                  <div className="flex items-center gap-2 w-full">
                    <span
                      className="text-base font-semibold truncate"
                      data-testid="upcoming-meet-name"
                    >
                      Upcoming Meet: {upcomingMeet.name}
                    </span>
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 h-4 flex-shrink-0 ${SPORT_BADGE_STYLES[upcomingSport]}`}
                      data-testid="upcoming-meet-sport-badge"
                    >
                      {getSportLabel(upcomingSport)}
                    </Badge>
                  </div>
                  <span
                    className="text-xs opacity-90 truncate w-full"
                    data-testid="upcoming-meet-schedule"
                  >
                    {scheduledLabel ? `${scheduledLabel} · ` : ''}Tap to Start
                  </span>
                </div>
              </Button>
            );
          })()}

          {/* Continue Live Meet / No Live Meet */}
          {liveMeet && !liveMeet.isOrphaned ? (
            <Button 
              onClick={() => navigate(`/meet/${liveMeet.meetId}`)}
              size="lg"
              className="w-full h-14 text-lg rounded-xl bg-green-600 hover:bg-green-700 text-white gap-2"
              data-testid="continue-live-meet-btn"
            >
              <Radio className="w-5 h-5 animate-pulse" />
              Continue Live Meet
            </Button>
          ) : (
            <div 
              className="w-full h-14 text-lg rounded-xl border border-dashed border-muted-foreground/30 flex items-center justify-center gap-2 text-muted-foreground"
              data-testid="no-live-meet-placeholder"
            >
              <Radio className="w-5 h-5" />
              <span className="text-sm">Live meet will appear here when running</span>
            </div>
          )}

          {/* Settings Button */}
          <Button 
            variant="outline"
            onClick={() => navigate('/settings')}
            size="lg"
            className="w-full h-14 text-lg rounded-xl gap-2"
            data-testid="settings-btn"
          >
            <Settings className="w-5 h-5" />
            Settings
          </Button>

          {/* App Tutorials Button */}
          <Button 
            variant="outline"
            onClick={() => window.open('https://foresightpulse.com', '_blank')}
            size="lg"
            className="w-full h-14 text-lg rounded-xl gap-2"
            data-testid="app-tutorials-btn"
          >
            App Tutorials
          </Button>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Meet?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteDialog.meetName}&rdquo;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-3 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialog({ open: false, meetId: null, meetName: '' })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteMeet}
              className="flex-1"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
